import type { CandleWithFlag } from "features/chart";

export function calcZigZag(
  data: CandleWithFlag[],
  _deviation: number,
  depth: number,
  backstep: number,
): (number | null)[] {
  const n = data.length;
  const result: (number | null)[] = new Array(n).fill(null);
  if (n < depth * 2 + 2) return result;

  const candidates: { i: number; price: number; isHigh: boolean }[] = [];
  for (let i = depth; i < n - depth; i++) {
    let isHigh = true, isLow = true;
    for (let j = i - depth; j <= i + depth; j++) {
      if (j === i) continue;
      if (data[j].high > data[i].high) isHigh = false;
      if (data[j].low < data[i].low) isLow = false;
    }
    if (isHigh) candidates.push({ i, price: data[i].high, isHigh: true });
    if (isLow) candidates.push({ i, price: data[i].low, isHigh: false });
  }
  candidates.sort((a, b) => a.i - b.i);

  const pivots: typeof candidates = [];
  for (const p of candidates) {
    if (pivots.length === 0) { pivots.push(p); continue; }
    const last = pivots[pivots.length - 1];
    if (p.isHigh === last.isHigh) {
      if ((p.isHigh && p.price > last.price) || (!p.isHigh && p.price < last.price)) {
        pivots[pivots.length - 1] = p;
      }
    } else if (p.i - last.i >= backstep) {
      pivots.push(p);
    }
  }

  for (const p of pivots) result[p.i] = p.price;

  // Live unconfirmed tail: extend from last confirmed pivot to current extreme
  if (pivots.length > 0) {
    const last = pivots[pivots.length - 1];
    if (last.isHigh) {
      let minVal = Infinity, minIdx = -1;
      for (let i = last.i + 1; i < n; i++) {
        if (data[i].low < minVal) { minVal = data[i].low; minIdx = i; }
      }
      if (minIdx !== -1) result[minIdx] = minVal;
    } else {
      let maxVal = -Infinity, maxIdx = -1;
      for (let i = last.i + 1; i < n; i++) {
        if (data[i].high > maxVal) { maxVal = data[i].high; maxIdx = i; }
      }
      if (maxIdx !== -1) result[maxIdx] = maxVal;
    }
  }

  return result;
}
